import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { 
  getBinders, 
  createBinder, 
  deleteBinder, 
  getFoldersStats, 
  getCachedFoldersStats, 
  invalidateFoldersStatsCache,
  getUserProfile,
  FoldersStatsResult 
} from '../../services/supabaseService';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { OfflineWarning } from '../../components/OfflineWarning';
import { supabase } from '../../lib/supabase';
import { motion, AnimatePresence } from 'motion/react';
import { useSettings } from '../../contexts/SettingsContext';

interface Folder {
  id: string;
  name: string;
  icon: string;
  color: string;
  cardCount: number;
  estimatedValue: number;
  isSystem?: boolean;
  gameName?: string;
}

const defaultStats: FoldersStatsResult = {
  system: { colecao: 0, wishlist: 0, offerlist: 0 },
  binders: {},
  estimatedValues: {
    system: { colecao: 0, wishlist: 0, offerlist: 0 },
    binders: {}
  }
};

export const FoldersPage: React.FC = () => {
  const { user, isOffline } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ownerId = searchParams.get('user');
  const isOwnProfile = !ownerId || (user && String(user.id) === String(ownerId));
  const effectiveUserId = ownerId || user?.id;

  const [ownerProfile, setOwnerProfile] = useState<any>(null);
  const [binders, setBinders] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [folderToDelete, setFolderToDelete] = useState<Folder | null>(null);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);
  const [dbGames, setDbGames] = useState<any[]>([]);
  const [selectedGameId, setSelectedGameId] = useState<string>('');
  const [isRefreshingStats, setIsRefreshingStats] = useState<boolean>(false);

  // Inicializa a partir do cache se disponível
  const [stats, setStats] = useState<FoldersStatsResult>(() => {
    if (effectiveUserId) {
      const cached = getCachedFoldersStats(effectiveUserId);
      if (cached) return cached;
    }
    return defaultStats;
  });

  useEffect(() => {
    if (ownerId && !isOwnProfile) {
      getUserProfile(ownerId).then((profile) => {
        setOwnerProfile(profile);
      }).catch(err => {
        console.warn('Erro ao carregar perfil do dono das pastas:', err);
      });
    } else {
      setOwnerProfile(null);
    }
  }, [ownerId, isOwnProfile]);

  useEffect(() => {
    const fetchGames = async () => {
      const { data } = await supabase.from('games').select('*');
      if (data) {
        setDbGames(data);
        if (data.length > 0) setSelectedGameId(data[0].id.toString());
      }
    };
    fetchGames();
  }, []);

  useEffect(() => {
    if (!effectiveUserId) return;

    const unsubscribe = getBinders(effectiveUserId, (dbBinders) => {
      setBinders(dbBinders || []);
    });

    return () => unsubscribe();
  }, [effectiveUserId]);

  const fetchStats = useCallback(async (force = false) => {
    if (!effectiveUserId) return;
    setIsRefreshingStats(true);
    try {
      if (force && isOwnProfile) {
        invalidateFoldersStatsCache(effectiveUserId);
      }

      // 1. Leitura atualizada dos fichários do usuário (apenas SELECT no banco)
      let resolvedId = effectiveUserId;
      if (typeof effectiveUserId === 'string' && effectiveUserId.length > 30) {
        const { data: u } = await supabase.from('users').select('id').eq('auth_id', effectiveUserId).maybeSingle();
        if (u) resolvedId = u.id;
      }

      const { data: freshBinders } = await supabase
        .from('user_binders')
        .select('*')
        .or(`user_id.eq.${resolvedId},user_id.eq.${effectiveUserId}`);
      if (freshBinders) {
        setBinders(freshBinders);
      }

      // 2. Leitura dos jogos disponíveis (apenas SELECT no banco)
      const { data: freshGames } = await supabase
        .from('games')
        .select('*');
      if (freshGames && freshGames.length > 0) {
        setDbGames(freshGames);
      }

      // 3. Leitura das estatísticas e contagens das pastas (apenas SELECT no banco)
      const s = await getFoldersStats(effectiveUserId, force);
      if (s) {
        setStats(s);
        if (force && isOwnProfile) {
          showNotification('Dados das pastas recarregados do banco com sucesso!', 'success');
        }
      }
    } catch (err) {
      console.error('Error fetching folder stats:', err);
      if (force && isOwnProfile) {
        showNotification('Erro ao recarregar dados das pastas.', 'error');
      }
    } finally {
      setIsRefreshingStats(false);
    }
  }, [effectiveUserId, isOwnProfile, showNotification]);

  useEffect(() => {
    if (!effectiveUserId) return;
    const cached = getCachedFoldersStats(effectiveUserId);
    if (cached) {
      setStats(cached);
    }
    fetchStats(false);
  }, [effectiveUserId, fetchStats]);

  const folders = useMemo<Folder[]>(() => {
    const systemFolders: Folder[] = [
      {
        id: 'colecao',
        name: 'Coleção',
        icon: 'fa-box-archive',
        color: 'bg-purple-600',
        cardCount: stats.system?.colecao || 0,
        estimatedValue: stats.estimatedValues?.system?.colecao || 0,
        isSystem: true
      },
      {
        id: 'wishlist',
        name: 'Wishlist',
        icon: 'fa-heart',
        color: 'bg-pink-600',
        cardCount: stats.system?.wishlist || 0,
        estimatedValue: stats.estimatedValues?.system?.wishlist || 0,
        isSystem: true
      },
      {
        id: 'offerlist',
        name: 'Offerlist',
        icon: 'fa-right-left',
        color: 'bg-emerald-600',
        cardCount: stats.system?.offerlist || 0,
        estimatedValue: stats.estimatedValues?.system?.offerlist || 0,
        isSystem: true
      },
    ];

    const customFolders: Folder[] = binders.map(b => ({
      id: b.id,
      name: b.name,
      icon: 'fa-folder',
      color: 'bg-slate-800',
      cardCount: stats.binders?.[b.id] || 0,
      estimatedValue: stats.estimatedValues?.binders?.[b.id] || 0,
      gameName: dbGames.find(g => g.id.toString() === b.game_id.toString())?.name || 'Personalizada'
    }));

    return [...systemFolders, ...customFolders];
  }, [stats, binders, dbGames]);

  const handleCreateBinder = async () => {
    if (!user || !newFolderName.trim() || !selectedGameId || !isOwnProfile) return;
    const game = dbGames.find(g => g.id.toString() === selectedGameId);
    try {
      await createBinder(user.id, newFolderName, selectedGameId, game?.name || '');
      invalidateFoldersStatsCache(user.id);
      showNotification(`Pasta "${newFolderName}" criada com sucesso!`, 'success');
      setIsModalOpen(false);
      setNewFolderName('');
      fetchStats(true);
    } catch (err: any) {
      console.error('Error creating binder:', err);
      showNotification(err.message || 'Erro ao criar pasta', 'error');
    }
  };

  const handleDeleteFolder = async () => {
    if (!user || !folderToDelete || !isOwnProfile) return;
    setIsDeletingFolder(true);
    try {
      await deleteBinder(user.id, folderToDelete.id);
      invalidateFoldersStatsCache(user.id);
      setBinders(prev => prev.filter(b => b.id.toString() !== folderToDelete.id.toString()));
      showNotification(`Pasta "${folderToDelete.name}" excluída com sucesso!`, 'success');
      setFolderToDelete(null);
      fetchStats(true);
    } catch (err: any) {
      console.error('Error deleting binder:', err);
      showNotification(err.message || 'Erro ao excluir pasta', 'error');
    } finally {
      setIsDeletingFolder(false);
    }
  };

  return (
    <div className="max-w-6xl 2xl:max-w-[1700px] mx-auto space-y-8 sm:space-y-10 animate-in fade-in duration-500 pb-20">
      {isOffline && <OfflineWarning />}

      {/* Header Diferenciado para Perfil Próprio vs Outro Usuário */}
      {isOwnProfile ? (
        <div className="flex items-start justify-between gap-3 sm:gap-4 px-2">
          <div className="space-y-1 min-w-0 flex-1">
            <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight leading-tight">Suas Pastas</h2>
          </div>
          <button 
            type="button"
            onClick={() => fetchStats(true)}
            disabled={isRefreshingStats}
            className="shrink-0 bg-slate-900/90 hover:bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-800/80 hover:border-slate-700 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl font-bold text-xs flex items-center space-x-1.5 sm:space-x-2 transition-all shadow-sm active:scale-95 disabled:opacity-50 cursor-pointer whitespace-nowrap"
            title="Recarregar dados das pastas diretamente do banco (apenas leitura)"
          >
            <i className={`fas fa-rotate text-purple-400 text-[11px] ${isRefreshingStats ? 'animate-spin' : ''}`}></i>
            <span>{isRefreshingStats ? 'Atualizando...' : 'Atualizar dados'}</span>
          </button>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/60 border border-slate-800 rounded-3xl p-5 sm:p-6 backdrop-blur-sm shadow-sm">
          <div className="flex items-center space-x-4 min-w-0">
            <Link
              to={`/perfil/${ownerId}`}
              className="w-10 h-10 sm:w-11 sm:h-11 bg-slate-950 hover:bg-slate-800 border border-slate-800 rounded-2xl flex items-center justify-center text-slate-400 hover:text-white transition-all shrink-0 active:scale-95"
              title="Voltar ao Perfil"
            >
              <i className="fas fa-arrow-left text-sm"></i>
            </Link>

            <Link to={`/perfil/${ownerId}`} className="w-12 h-12 rounded-2xl bg-slate-800 overflow-hidden border border-white/10 shrink-0 block">
              {ownerProfile?.avatar ? (
                <img src={ownerProfile.avatar} alt={ownerProfile.username} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-600">
                  <i className="fas fa-user text-lg"></i>
                </div>
              )}
            </Link>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg sm:text-2xl font-black text-white uppercase tracking-tight truncate">
                  Pastas de {ownerProfile?.fullname || ownerProfile?.username || 'Colecionador'}
                </h2>
                <span className="text-[9px] font-black uppercase tracking-wider bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full border border-purple-500/30 shrink-0">
                  Visualização
                </span>
              </div>
              <p className="text-xs text-slate-400 truncate">
                @{ownerProfile?.username} • Fichários públicos
              </p>
            </div>
          </div>
        </div>
      )}

      <section className="space-y-6">
        <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] px-2">Pastas do Sistema</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 2xl:grid-cols-3 gap-6">
          {folders.filter(f => f.isSystem).map(folder => (
            <FolderCard 
              key={folder.id} 
              folder={folder} 
              onClick={() => navigate(`/pastas/${folder.id}${ownerId ? `?user=${ownerId}` : ''}`)} 
            />
          ))}
        </div>
      </section>

      <section className="space-y-6 pt-4">
        <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] px-2">Pastas Personalizadas</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
          {folders.filter(f => !f.isSystem).map(folder => (
            <FolderCard 
              key={folder.id} 
              folder={folder} 
              onClick={() => navigate(`/pastas/${folder.id}${ownerId ? `?user=${ownerId}` : ''}`)} 
              onDelete={isOwnProfile ? () => setFolderToDelete(folder) : undefined}
            />
          ))}
          {isOwnProfile && (
            <button 
              onClick={() => setIsModalOpen(true)}
              className="border-2 border-dashed border-slate-800 rounded-[32px] p-8 flex flex-col items-center justify-center text-center space-y-4 hover:border-purple-500/30 hover:bg-purple-500/5 transition-all group cursor-pointer"
            >
               <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center text-slate-700 group-hover:text-purple-500 transition-colors">
                  <i className="fas fa-plus"></i>
               </div>
               <p className="text-xs font-black text-slate-600 uppercase tracking-widest">Criar Nova Pasta</p>
            </button>
          )}
          {!isOwnProfile && folders.filter(f => !f.isSystem).length === 0 && (
            <div className="col-span-full py-10 px-6 text-center text-slate-500 text-xs italic bg-slate-900/30 border border-slate-800/60 rounded-3xl space-y-2">
              <i className="fas fa-folder-open text-2xl text-slate-700 block"></i>
              <p>Este colecionador não possui pastas personalizadas públicas no momento.</p>
            </div>
          )}
        </div>
      </section>

      {/* Modal de Exclusão de Pasta Personalizada */}
      <AnimatePresence>
        {isOwnProfile && folderToDelete && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-3xl p-6 md:p-8 space-y-6 shadow-2xl relative"
            >
              <div className="w-12 h-12 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center mx-auto text-xl">
                <i className="fas fa-trash-can"></i>
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-xl font-black text-white">Excluir Pasta?</h3>
                <p className="text-xs text-slate-400">
                  Tem certeza que deseja excluir a pasta <span className="text-white font-bold">"{folderToDelete.name}"</span>? As cartas dentro dela não serão apagadas da sua coleção geral.
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setFolderToDelete(null)}
                  disabled={isDeletingFolder}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-3 rounded-xl transition-colors cursor-pointer text-xs uppercase tracking-wider"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDeleteFolder}
                  disabled={isDeletingFolder}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer text-xs uppercase tracking-wider flex items-center justify-center gap-2"
                >
                  {isDeletingFolder ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      <span>Excluindo...</span>
                    </>
                  ) : (
                    <span>Excluir</span>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de Nova Pasta */}
      {isOwnProfile && isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-[32px] p-8 w-full max-w-md space-y-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black text-white uppercase tracking-tight">Nova Pasta</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-500 hover:text-white transition-colors cursor-pointer">
                <i className="fas fa-xmark text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Nome da Pasta</label>
                <input 
                  type="text" 
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Ex: Trocas One Piece"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Jogo</label>
                <select 
                  value={selectedGameId}
                  onChange={(e) => setSelectedGameId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                >
                  {dbGames.map(game => (
                    <option key={game.id} value={game.id}>{game.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <button 
              onClick={handleCreateBinder}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-black py-4 rounded-xl transition-all shadow-lg shadow-purple-600/20 active:scale-95 cursor-pointer"
            >
              Criar Pasta
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const FolderCard: React.FC<{ 
  folder: Folder; 
  onClick?: () => void;
  onDelete?: () => void;
}> = ({ folder, onClick, onDelete }) => {
  const { settings, formatPrice } = useSettings();
  return (
  <div 
    onClick={onClick}
    className="group bg-slate-900/40 border border-slate-800 rounded-[32px] p-6 hover:border-purple-500/50 transition-all duration-300 cursor-pointer shadow-xl relative overflow-hidden"
  >
    <div className={`absolute top-0 right-0 w-32 h-32 ${folder.color} opacity-[0.03] rounded-full blur-3xl -mr-10 -mt-10`}></div>
    
    {!folder.isSystem && onDelete && (
      <button
        type="button"
        title="Excluir pasta"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute top-5 right-5 w-8 h-8 rounded-xl bg-slate-800/80 hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-slate-700/60 hover:border-red-500/40 flex items-center justify-center transition-all opacity-80 md:opacity-0 md:group-hover:opacity-100 z-10 cursor-pointer shadow-sm active:scale-90"
      >
        <i className="fas fa-trash-can text-xs"></i>
      </button>
    )}

    <div className="flex items-center space-x-5 mb-8">
       <div className={`w-14 h-14 rounded-2xl ${folder.color} flex items-center justify-center text-white text-xl shadow-lg shadow-black/20 group-hover:scale-110 transition-transform`}>
          <i className={`fas ${folder.icon}`}></i>
       </div>
       <div className="pr-6 truncate">
          <h4 className="text-lg font-black text-white uppercase tracking-tight leading-none mb-1 truncate">{folder.name}</h4>
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            {folder.isSystem ? 'Principal' : folder.gameName || 'Personalizada'}
          </span>
       </div>
    </div>

    <div className="grid grid-cols-2 gap-4 border-t border-slate-800/50 pt-6">
       <div>
          <p className="text-[9px] font-black text-slate-600 uppercase tracking-widest mb-1">Cartas</p>
          <p className="text-lg font-black text-slate-200">{folder.cardCount}</p>
       </div>
       <div className="text-right">
          <p className="text-[9px] font-black text-slate-600 uppercase tracking-widest mb-1">Valor Est.</p>
          <p className="text-lg font-black text-emerald-400">
            {settings.showCardPrices ? formatPrice(folder.estimatedValue || 0) : '—'}
          </p>
       </div>
    </div>
  </div>
  );
};
